/**
 * Eye Care Mode — Reduces eye strain for long working hours
 *
 * Modes:
 * - off: Normal display
 * - warm: Warm yellow tint (reduces blue light, like f.lux/Night Light)
 * - sepia: Paper-like sepia tone (like reading mode)
 * - highContrast: High contrast B&W (maximum readability)
 */

export type EyeCareMode = 'off' | 'warm' | 'sepia' | 'highContrast';

const FILTERS: Record<EyeCareMode, string> = {
  off: 'none',
  warm: 'sepia(15%) saturate(90%) brightness(98%)',
  sepia: 'sepia(35%) saturate(80%) brightness(95%)',
  highContrast: 'grayscale(100%) contrast(120%) brightness(95%)',
};

let currentMode: EyeCareMode = 'off';

export function setEyeCareMode(mode: EyeCareMode): void {
  currentMode = mode;
  const root = document.documentElement;
  root.style.filter = FILTERS[mode];
  root.setAttribute('data-eyecare', mode);
  localStorage.setItem('aarthika_eyecare', mode);
}

export function getEyeCareMode(): EyeCareMode {
  return currentMode;
}

export function initEyeCare(): void {
  const saved = localStorage.getItem('aarthika_eyecare') as EyeCareMode | null;
  if (saved && FILTERS[saved]) {
    setEyeCareMode(saved);
  }
}

export function getEyeCareModes(): { value: EyeCareMode; label: string; desc: string }[] {
  return [
    { value: 'off', label: 'Off', desc: 'Normal display' },
    { value: 'warm', label: 'Warm Light', desc: 'Reduces blue light — easier on eyes at night' },
    { value: 'sepia', label: 'Sepia', desc: 'Paper-like tone — comfortable for long reading' },
    { value: 'highContrast', label: 'High Contrast', desc: 'Black & white — maximum clarity' },
  ];
}
